import { Helmet } from "react-helmet-async";
import Navbar from "@/components/landing/Navbar";
import HeroSection from "@/components/landing/HeroSection";
import EmissionsEstimator from "@/components/landing/EmissionsEstimator";
import FeaturesSection from "@/components/landing/FeaturesSection";
import StatsSection from "@/components/landing/StatsSection";
import HowItWorksSection from "@/components/landing/HowItWorksSection";
import ProductDemoSection from "@/components/landing/ProductDemoSection";
import FAQSection from "@/components/landing/FAQSection";
import ArticlesSection from "@/components/landing/ArticlesSection";
import TestimonialsSection from "@/components/landing/TestimonialsSection";
import CTASection from "@/components/landing/CTASection";
import Footer from "@/components/landing/Footer";
import { useLanguage } from "@/contexts/LanguageContext";

const Index = () => {
  const { language } = useLanguage();

  const metadata = {
    en: {
      title: "Carbonwize | Audit-Grade Carbon Management Platform for Enterprises",
      description: "Scope 1-3 carbon accounting aligned with TGO & ISO 14064. Trusted by 500+ enterprises for compliance-ready reports in minutes.",
      locale: "en_US"
    },
    th: {
      title: "Carbonwize | ระบบจัดการคาร์บอนระดับองค์กร",
      description: "CFO Platform ที่ได้รับการรับรองโดย อบก. และ SET สำหรับการจัดทำบัญชีคาร์บอน Scope 1-3 ตามมาตรฐาน TGO CFO และ ISO 14064 พร้อมออกรายงานจากระบบและทวนสอบในที่เดียว",
      locale: "th_TH"
    }
  };

  const ogTags = {
    title: "Carbonwize | Audit-Grade Carbon Management for Enterprises",
    description: "Automate carbon accounting with enterprise-grade compliance, precision, and automation. Trusted by 500+ enterprises."
  };

  return (
    <>
      <Helmet>
        <title>{metadata[language].title}</title>
        <meta name="description" content={metadata[language].description} />
        <meta name="keywords" content="ซอฟต์แวร์คาร์บอน, แพลตฟอร์มจัดการคาร์บอน, carbon accounting Thailand, คาร์บอนฟุตพริ้นท์องค์กร, CFO องค์กร, TGO certified, ISO 14064, GHG protocol, ESG reporting Thailand, SET carbon platform, ก๊าซเรือนกระจก, รายงาน ESG, net zero Thailand, CSRD compliance, SEC climate disclosure, carbon footprint software, sustainability software Thailand, บริษัทจดทะเบียน SET, ตลาดหลักทรัพย์, คาร์บอนเครดิต" />
        <link rel="canonical" href="https://carbonwize.io" />
        
        {/* hreflang tags for multilingual SEO */}
        <link rel="alternate" hrefLang="en" href="https://carbonwize.io/en/" />
        <link rel="alternate" hrefLang="th" href="https://carbonwize.io/th/" />
        <link rel="alternate" hrefLang="x-default" href="https://carbonwize.io/" />
        
        {/* Language & Region */}
        <meta name="geo.region" content="TH" />
        <meta name="geo.placename" content="Bangkok, Thailand" />
        
        {/* Open Graph - Standardized Brand Tags */}
        <meta property="og:title" content={ogTags.title} />
        <meta property="og:description" content={ogTags.description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://carbonwize.io" />
        <meta property="og:image" content="https://carbonwize.io/og-thumbnail.png" />
        <meta property="og:locale" content={metadata[language].locale} />
        <meta property="og:site_name" content="Carbonwize" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={ogTags.title} />
        <meta name="twitter:description" content={ogTags.description} />
        <meta name="twitter:image" content="https://carbonwize.io/og-thumbnail.png" />
        
        {/* Structured Data - Organization (Enhanced) */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Carbonwize",
            "alternateName": "คาร์บอนไวซ์",
            "url": "https://carbonwize.io",
            "logo": {
              "@type": "ImageObject",
              "url": "https://carbonwize.io/carbonwize-logo.png",
              "width": 200,
              "height": 60
            },
            "image": "https://carbonwize.io/og-thumbnail.png",
            "description": "Automate carbon accounting with enterprise-grade compliance, precision, and automation. Trusted by 500+ enterprises.",
            "slogan": "ซอฟต์แวร์คาร์บอน อันดับ 1 ประเทศไทย",
            "foundingDate": "2020",
            "areaServed": [
              { "@type": "Country", "name": "Thailand" },
              { "@type": "Place", "name": "Southeast Asia" },
              { "@type": "Place", "name": "Asia Pacific" }
            ],
            "address": {
              "@type": "PostalAddress",
              "addressCountry": "TH",
              "addressLocality": "Bangkok",
              "addressRegion": "Bangkok"
            },
            "contactPoint": [
              {
                "@type": "ContactPoint",
                "contactType": "sales",
                "availableLanguage": ["Thai", "English"],
                "url": "https://carbonwize.io/contact-us"
              },
              {
                "@type": "ContactPoint",
                "contactType": "customer support",
                "availableLanguage": ["Thai", "English"]
              }
            ],
            "sameAs": [
              "https://www.linkedin.com/company/carbonwize",
              "https://www.facebook.com/carbonwize"
            ],
            "knowsAbout": [
              "Carbon Accounting",
              "GHG Protocol",
              "ISO 14064",
              "ESG Reporting",
              "Carbon Footprint",
              "Net Zero Strategy",
              "CSRD Compliance",
              "TGO Standards"
            ],
            "hasCredential": [
              {
                "@type": "EducationalOccupationalCredential",
                "credentialCategory": "certification",
                "name": "TGO Certified Partner"
              },
              {
                "@type": "EducationalOccupationalCredential",
                "credentialCategory": "partnership",
                "name": "SET Official Partner"
              }
            ]
          })}
        </script>
        
        {/* Structured Data - Product (Software) */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            "name": "Carbonwize Carbon Management Platform",
            "alternateName": "Carbonwize ซอฟต์แวร์คาร์บอน",
            "description": metadata[language].description,
            "url": "https://carbonwize.io",
            "image": "https://carbonwize.io/og-thumbnail.png",
            "brand": {
              "@type": "Brand",
              "name": "Carbonwize",
              "logo": "https://carbonwize.io/carbonwize-logo.png"
            },
            "category": "Business Software > Environmental Management > Carbon Accounting",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "THB",
              "priceValidUntil": "2025-12-31",
              "availability": "https://schema.org/InStock",
              "description": "นัดสาธิตฟรี ไม่มีค่าใช้จ่าย",
              "seller": {
                "@type": "Organization",
                "name": "Carbonwize"
              }
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.9",
              "reviewCount": "500",
              "bestRating": "5",
              "worstRating": "1"
            },
            "review": [
              {
                "@type": "Review",
                "reviewRating": {
                  "@type": "Rating",
                  "ratingValue": "5",
                  "bestRating": "5"
                },
                "author": {
                  "@type": "Person",
                  "name": "คุณจุฑากาญจน์ ศิริวัฒน์"
                },
                "reviewBody": "ใช้งานง่าย ตอบโจทย์การรายงานคาร์บอนฟุตพริ้นท์ขององค์กร"
              }
            ],
            "audience": {
              "@type": "BusinessAudience",
              "audienceType": "SET Listed Companies, Large Enterprises, ESG-focused Organizations"
            },
            "additionalProperty": [
              {
                "@type": "PropertyValue",
                "name": "Supported Standards",
                "value": "TGO, ISO 14064, GHG Protocol, CSRD, SEC Climate Disclosure"
              },
              {
                "@type": "PropertyValue",
                "name": "Active Users",
                "value": "500+ Organizations"
              },
              {
                "@type": "PropertyValue",
                "name": "Supported Languages",
                "value": "Thai, English"
              }
            ]
          })}
        </script>
        
        {/* Structured Data - SoftwareApplication */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "Carbonwize",
            "applicationCategory": "BusinessApplication",
            "applicationSubCategory": "Environmental Management Software",
            "description": metadata[language].description,
            "operatingSystem": "Web-based (All platforms)",
            "inLanguage": ["th", "en"],
            "softwareVersion": "2.0",
            "featureList": [
              "Automated Carbon Calculation",
              "Real-time Dashboard & Analytics",
              "Multi-standard Reporting (TGO, ISO 14064, GHG Protocol)",
              "Scope 1, 2, 3 Emissions Tracking",
              "ESG Report Generation",
              "Third-party Verification Support"
            ],
            "screenshot": "https://carbonwize.io/dashboard-screenshot.png",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "THB",
              "description": "Free Demo Available"
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.9",
              "ratingCount": "500",
              "bestRating": "5"
            },
            "provider": {
              "@type": "Organization",
              "name": "Carbonwize",
              "url": "https://carbonwize.io"
            }
          })}
        </script>
        
        {/* Structured Data - FAQ */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              { "@type": "Question", "name": "What is Carbonwize?", "acceptedAnswer": { "@type": "Answer", "text": "Carbonwize is an enterprise carbon accounting platform for Scope 1–3 emissions reporting." }},
              { "@type": "Question", "name": "What are Scope 1, 2, and 3?", "acceptedAnswer": { "@type": "Answer", "text": "Scope 1, 2, and 3 represent direct, indirect energy, and value-chain emissions." }}
            ]
          })}
        </script>
        
        {/* Structured Data - WebSite with SearchAction */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "Carbonwize",
            "alternateName": "คาร์บอนไวซ์",
            "url": "https://carbonwize.io",
            "inLanguage": ["th", "en"],
            "publisher": {
              "@type": "Organization",
              "name": "Carbonwize"
            }
          })}
        </script>
        
        {/* Structured Data - BreadcrumbList */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://carbonwize.io"
              }
            ]
          })}
        </script>
      </Helmet>

      <main className="min-h-screen">
        <Navbar />
        <HeroSection />
        <EmissionsEstimator />
        <FeaturesSection />
        <HowItWorksSection />
        <ProductDemoSection />
        <StatsSection />
        <TestimonialsSection />
        <CTASection />
        <ArticlesSection />
        <FAQSection />
        <Footer />
      </main>
    </>
  );
};

export default Index;
